/**
 * AbandonedBelongingsSection — cleaner-side section on TaskDetailPage
 * for filing an abandoned-belongings report and viewing the rows
 * already filed against this task.
 *
 * Tenant-toggled: hidden entirely when tenant_settings.feature_flags
 * does NOT have abandoned_belongings_tracking=true. Mirrors the
 * additional-fee section's structure so contract-layer surfaces stay
 * shaped the same way.
 *
 * Photo attachment uses the same "schema column ships now, uploader
 * grows in next iteration" pattern as FeeRequestSection — the
 * description field is the v1 required input; storage paths are
 * passed through but the in-section uploader lands when the cleaner
 * mobile flow is field-validated.
 */

import { useState, type ReactNode } from 'react'
import { useTranslation }            from 'react-i18next'
import { useReportAbandonedBelongings } from '@/hooks/useReportAbandonedBelongings'
import {
  useAbandonedBelongingsReports,
  type AbandonedBelongingsReportRow,
  type AbandonedBelongingsStatus,
}                                       from '@/hooks/useAbandonedBelongingsReports'
import { useTenantFeatures }            from '@/hooks/useTenantFeatures'
import { isAppError }                   from '@/lib/errors/AppError'

interface Props {
  taskId:        string
  taskStatus:    string
  /** True only on the cleaner's own task view — drives the report form. */
  canReport:     boolean
}

export function AbandonedBelongingsSection({ taskId, taskStatus, canReport }: Props): ReactNode {
  const { t } = useTranslation()
  const { has: hasFeature, isLoading: flagsLoading } = useTenantFeatures()
  const { data, isLoading } = useAbandonedBelongingsReports({ cleaningTaskId: taskId })

  // Tenant feature gate: hidden entirely when the contract clause is
  // off. Defaults closed while the flag-fetch is in flight to avoid
  // flashing a UI the tenant cannot actually use.
  if (flagsLoading) return null
  if (!hasFeature('abandoned_belongings_tracking')) return null

  const reports  = data ?? []
  const showForm = canReport && (taskStatus === 'pending' || taskStatus === 'in_progress')

  if (!showForm && reports.length === 0 && !isLoading) return null

  return (
    <section
      data-testid="abandoned-belongings-section"
      aria-labelledby="abandoned-belongings-heading"
      style={{
        background:    'var(--brand-white)',
        border:        '1px solid var(--brand-neutral-300)',
        borderRadius:  'var(--brand-radius-lg)',
        padding:       '1rem',
        display:       'flex',
        flexDirection: 'column',
        gap:           '0.6rem',
      }}
    >
      <h2 id="abandoned-belongings-heading" style={{ margin: 0, fontSize: '1rem' }}>
        {t('abandonedBelongings.heading', 'Abandoned belongings')}
      </h2>

      {reports.length > 0 && (
        <ul data-testid="abandoned-belongings-row-list" style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {reports.map((r) => <ExistingRow key={r.id} row={r} />)}
        </ul>
      )}

      {showForm && <ReportForm taskId={taskId} />}
    </section>
  )
}

function ExistingRow({ row }: { row: AbandonedBelongingsReportRow }): ReactNode {
  return (
    <li
      data-testid="abandoned-belongings-existing"
      data-report-id={row.id}
      data-status={row.status}
      style={{
        display:       'flex',
        gap:           '0.5rem',
        padding:       '0.4rem 0',
        borderBottom:  '1px solid var(--brand-neutral-100)',
        alignItems:    'baseline',
      }}
    >
      <StatusChip status={row.status} />
      <span style={{ color: 'var(--brand-neutral-700)', fontSize: '0.9rem', flex: 1 }}>
        {row.description}
      </span>
    </li>
  )
}

function StatusChip({ status }: { status: AbandonedBelongingsStatus }): ReactNode {
  return (
    <span
      data-testid="abandoned-belongings-status-chip"
      data-status={status}
      style={{
        display:      'inline-block',
        padding:      '0.1rem 0.45rem',
        borderRadius: 'var(--brand-radius-full)',
        background:   'var(--brand-neutral-100)',
        color:        'var(--brand-neutral-700)',
        fontSize:     '0.7rem',
        fontWeight:   600,
      }}
    >
      {status}
    </span>
  )
}

function ReportForm({ taskId }: { taskId: string }): ReactNode {
  const { t } = useTranslation()
  const report = useReportAbandonedBelongings()
  const [open, setOpen]             = useState(false)
  const [description, setDescription] = useState('')

  const descTrimmed = description.trim()
  const descValid   = descTrimmed.length > 0 && description.length <= 1000

  async function submit(): Promise<void> {
    if (!descValid) return
    try {
      await report.mutateAsync({
        cleaningTaskId: taskId,
        description:    descTrimmed,
      })
      setOpen(false)
      setDescription('')
    } catch { /* error rendered via report.error */ }
  }

  const errMsg = report.error && isAppError(report.error) ? report.error.userMessage : null

  if (!open) {
    return (
      <button
        type="button"
        data-testid="abandoned-belongings-open"
        onClick={() => setOpen(true)}
        style={{
          alignSelf:    'flex-start',
          padding:      '0.4rem 0.8rem',
          background:   'transparent',
          color:        'var(--brand-primary)',
          border:       '1px dashed var(--brand-primary)',
          borderRadius: 'var(--brand-radius-sm)',
          fontSize:     '0.85rem',
          cursor:       'pointer',
        }}
      >
        {t('abandonedBelongings.openForm', '+ Report abandoned belongings')}
      </button>
    )
  }

  return (
    <div
      data-testid="abandoned-belongings-form"
      style={{
        display:        'flex',
        flexDirection:  'column',
        gap:            '0.5rem',
        padding:        '0.6rem',
        background:     'var(--brand-neutral-50, var(--brand-neutral-100))',
        borderRadius:   'var(--brand-radius-sm)',
      }}
    >
      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
        <span style={{ fontSize: '0.8rem', color: 'var(--brand-neutral-700)' }}>
          {t('abandonedBelongings.descriptionLabel', 'Description (1–1000 chars)')}
        </span>
        <textarea
          data-testid="abandoned-belongings-description-input"
          value={description}
          onChange={(e) => setDescription(e.target.value.slice(0, 1000))}
          rows={3}
          maxLength={1000}
          style={{
            fontSize:     '0.9rem',
            padding:      '0.4rem',
            borderRadius: 'var(--brand-radius-sm)',
            border:       '1px solid var(--brand-neutral-300)',
            resize:       'vertical',
          }}
        />
      </label>
      <div style={{ display: 'flex', gap: '0.4rem' }}>
        <button
          type="button"
          data-testid="abandoned-belongings-submit"
          onClick={() => { void submit() }}
          disabled={report.isPending || !descValid}
          style={{
            padding:      '0.4rem 0.8rem',
            background:   report.isPending || !descValid
                            ? 'var(--brand-neutral-300)'
                            : 'var(--brand-primary)',
            color:        'var(--brand-white)',
            border:       'none',
            borderRadius: 'var(--brand-radius-sm)',
            fontSize:     '0.85rem',
            fontWeight:   600,
            cursor:       report.isPending || !descValid ? 'not-allowed' : 'pointer',
          }}
        >
          {t('abandonedBelongings.submit', 'File report')}
        </button>
        <button
          type="button"
          data-testid="abandoned-belongings-cancel"
          onClick={() => { setOpen(false); setDescription('') }}
          style={{
            padding:      '0.4rem 0.8rem',
            background:   'transparent',
            color:        'var(--brand-neutral-700)',
            border:       '1px solid var(--brand-neutral-300)',
            borderRadius: 'var(--brand-radius-sm)',
            fontSize:     '0.85rem',
            cursor:       'pointer',
          }}
        >
          {t('abandonedBelongings.cancel', 'Cancel')}
        </button>
      </div>
      {errMsg && (
        <p role="alert" data-testid="abandoned-belongings-error" style={{ color: 'var(--brand-danger)', fontSize: '0.85rem', margin: 0 }}>
          {errMsg}
        </p>
      )}
    </div>
  )
}
